import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import logoUrl from '../../assets/images/Senerfi-formato.png';
import { getQualityPrintMeta } from './qualityPrintConfig';

function esc(v) {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fmtMoney(n) {
  const v = Number(n || 0);
  try {
    return v.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch (_) {
    const fixed = (Math.round(v * 100) / 100).toFixed(2);
    const [a, b] = fixed.split('.');
    const withSep = String(a).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${withSep},${b || '00'}`;
  }
}

async function openPurchaseOrderFrame(data) {
  const qualityMeta = await getQualityPrintMeta('purchase_order');
  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.name = 'PRINT_OC';
  document.body.appendChild(frame);

  const w = frame.contentWindow;
  const doc = w.document;
  doc.open();

  const company = data.company || {};
  const order = data.order || {};
  const req = data.request || {};
  const meta = data.meta || {};
  const items = Array.isArray(req.items) ? req.items : [];

  // imp_cargo_pct/imp_retention_pct siempre llegan como número de
  // porcentaje plano (1 = 1%, 19 = 19%, 0.1 = 0.1%), nunca como razón ya
  // dividida — el antiguo "n > 1 ? n/100 : n" confundía un 1% (o 0.1%)
  // exacto con una razón ya dividida, corrompiendo la retención mostrada.
  const pctToRatio = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return 0;
    return n / 100;
  };

  const cargoRatio = pctToRatio(meta.imp_cargo_pct ?? meta.imp_cargo_rate ?? 0);
  const retentionRatio = pctToRatio(meta.imp_retention_pct ?? meta.imp_retention_rate ?? 0);
  const cargoLabel = String(meta.imp_cargo_label || '').trim();
  const retentionLabel = String(meta.imp_retention_label || '').trim();

  const calc = items.reduce((acc, it) => {
    const qty = Number(it.quantity || 0) || 0;
    const price = Number(it.price || 0) || 0;
    const discount = 0;
    const base = Number(it.subtotal ?? ((qty * price) - discount)) || 0;
    const itemCargoRatio = pctToRatio(it.imp_cargo_pct ?? it.imp_cargo_rate ?? meta.imp_cargo_pct ?? meta.imp_cargo_rate ?? 0);
    const itemRetentionRatio = pctToRatio(it.imp_retention_pct ?? it.imp_retention_rate ?? meta.imp_retention_pct ?? meta.imp_retention_rate ?? 0);
    const cargo = Number(it.imp_cargo_value ?? (base * itemCargoRatio)) || 0;
    const retention = Number(it.imp_retention_value ?? (base * itemRetentionRatio)) || 0;
    const lineTotal = Number(it.total ?? (base + cargo - retention)) || 0;
    acc.subtotal += base;
    acc.cargo += cargo;
    acc.retention += retention;
    acc.totalLines += lineTotal;
    return acc;
  }, { subtotal: 0, cargo: 0, retention: 0, totalLines: 0 });

  const total = calc.totalLines;

  const title = `OC_${String(order.code || 'OC').replace(/\s+/g, '_')}`;
  const basePath = (process.env.PUBLIC_URL || '/').replace(/\/+$/g, '');
  const abs = (p) => {
    const s = String(p || '').trim();
    if (!s) return '';
    if (/^data:image\//i.test(s)) return s;
    if (/^https?:\/\//i.test(s)) return s;
    if (s.startsWith('/')) return s;
    return `${basePath || ''}/${s}`.replace(/\/{2,}/g, '/');
  };
  const tryToDataUrl = async (src) => {
    const u = abs(src);
    if (!u) return '';
    if (/^data:image\//i.test(u)) return u;
    try {
      const resp = await fetch(u, { cache: 'no-store' });
      if (!resp.ok) return '';
      const blob = await resp.blob();
      if (blob && blob.type && !String(blob.type).toLowerCase().startsWith('image/')) return '';
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result || ''));
        reader.readAsDataURL(blob);
      });
    } catch (_) {
      return '';
    }
  };
  const candidates = [
    String(data.logoUrl || '').trim(),
    `${basePath || ''}${logoUrl}`,
    `${basePath || ''}/Senerfi-formato.png`,
  ].filter(Boolean);
  let logoSrc = '';
  for (const c of candidates) {
    const got = await tryToDataUrl(c);
    if (got) { logoSrc = got; break; }
  }
  const approvalSigSrc = await tryToDataUrl(meta.approval_signature || meta.approval_signature_url || '');
  const qualityVersion = String(qualityMeta?.version || '').trim();
  const qualityDate = String(qualityMeta?.effective_date_display || '').trim();

  const rows = items.map((it, idx) => {
    const qty = Number(it.quantity || 0) || 0;
    const price = Number(it.price || 0) || 0;
    const discount = 0;
    const base = Number(it.subtotal ?? ((qty * price) - discount)) || 0;
    const itemCargoRatio = pctToRatio(it.imp_cargo_pct ?? it.imp_cargo_rate ?? meta.imp_cargo_pct ?? meta.imp_cargo_rate ?? 0);
    const itemRetentionRatio = pctToRatio(it.imp_retention_pct ?? it.imp_retention_rate ?? meta.imp_retention_pct ?? meta.imp_retention_rate ?? 0);
    const cargo = Number(it.imp_cargo_value ?? (base * itemCargoRatio)) || 0;
    const retention = Number(it.imp_retention_value ?? (base * itemRetentionRatio)) || 0;
    const lineTotal = Number(it.total ?? (base + cargo - retention)) || 0;
    const label = it.material_code ? `${it.material_code} - ${it.material_name || it.description || ''}` : (it.description || '');
    return `
      <tr>
        <td class="td center">${idx + 1}</td>
        <td class="td">${esc(label || '-')}</td>
        <td class="td right">${esc(qty)}</td>
        <td class="td right">${esc(fmtMoney(price))}</td>
        <td class="td right">${esc(fmtMoney(discount))}</td>
        <td class="td right">${esc(fmtMoney(cargo))}</td>
        <td class="td right">${esc(fmtMoney(retention))}</td>
        <td class="td right">${esc(fmtMoney(lineTotal))}</td>
      </tr>
    `;
  }).join('');

  const quoteImages = Array.isArray(meta.quote_images) ? meta.quote_images : [];
  const attachmentsHtml = quoteImages.length
    ? `
      <div class="box p12" style="margin-top:12px">
        <div class="small"><b>Soportes de cotización</b></div>
        <div class="attach-grid">
          ${quoteImages.map((src) => `<div class="attach"><img src="${esc(src)}" /></div>`).join('')}
        </div>
      </div>
    `
    : '';

  const headerLogo = logoSrc ? `<img src="${esc(logoSrc)}" style="height:54px; object-fit:contain" />` : `<div style="font-weight:800;font-size:22px;letter-spacing:.5px">${esc(company.name || 'SENERFI S.A.S.')}</div>`;

  doc.write(`<!DOCTYPE html>
  <html>
  <head>
    <meta charset="utf-8" />
    <title>${esc(title)}</title>
    <style>
      * { box-sizing: border-box; }
      body { margin: 0; font-family: Arial, Helvetica, sans-serif; background: #fff; color: #111827; }
      .sheet { width: 900px; padding: 18px; }
      .top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
      .box { border: 1px solid #111827; }
      .p12 { padding: 12px; }
      .title { font-size: 20px; font-weight: 800; text-transform: uppercase; text-align: right; }
      .muted { color: #4b5563; font-size: 12px; }
      .kv { display: grid; grid-template-columns: 120px 1fr; gap: 6px 10px; font-size: 12px; }
      .kv b { font-weight: 700; }
      table { width: 100%; border-collapse: collapse; }
      .th { border: 1px solid #111827; background: #f3f4f6; padding: 6px; font-size: 12px; text-align: left; }
      .td { border: 1px solid #111827; padding: 6px; font-size: 12px; vertical-align: top; }
      .right { text-align: right; }
      .center { text-align: center; }
      .grid2 { display: grid; grid-template-columns: 1fr 280px; gap: 12px; margin-top: 12px; }
      .totals { width: 100%; }
      .totals td { padding: 6px; font-size: 12px; }
      .totals .lab { color: #374151; }
      .totals .val { text-align: right; font-weight: 700; }
      .foot { margin-top: 10px; display: grid; grid-template-columns: 1fr 1fr; gap: 12px; font-size: 12px; }
      .small { font-size: 11px; color: #374151; }
      .quality { margin-top: 10px; font-size: 11px; color: #4b5563; text-align: right; }
      .attach-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 8px; }
      .attach { border: 1px solid #111827; padding: 6px; }
      .attach img { width: 100%; max-height: 260px; object-fit: contain; display: block; }
    </style>
  </head>
  <body>
    <div class="sheet">
      <div class="top">
        <div>
          ${headerLogo}
          <div class="muted">${esc(company.subtitle || 'Soluciones energéticas forestales integrales')}</div>
          <div class="muted">${esc(company.address || '')}</div>
        </div>
        <div class="box p12" style="min-width: 320px">
          <div class="title">Orden de compra</div>
          <div class="kv" style="margin-top:8px">
            <div><b>No.</b></div><div>${esc(order.code || 'OC-—')}</div>
            <div><b>Fecha</b></div><div>${esc(order.issueDate || meta.issue_date || '')}</div>
            <div><b>Para</b></div><div>${esc(meta.provider || order.provider || '')}</div>
            <div><b>NIT</b></div><div>${esc(meta.provider_nit || '')}</div>
          </div>
        </div>
      </div>

      <div class="box p12" style="margin-top:12px">
        <div class="kv">
          <div><b>Solicitud</b></div><div>${esc(req.code || meta.request_code || '')}</div>
          <div><b>Área</b></div><div>${esc(req.area || '')}</div>
          <div><b>Solicitante</b></div><div>${esc(req.requesterName || '')}</div>
          <div><b>Centro de costo</b></div><div>${esc(req.costCenter || '')}</div>
          <div><b>Método de pago</b></div><div>${esc(meta.payment_method || '')}</div>
          <div><b>Fecha de entrega</b></div><div>${esc(meta.delivery_date || req.requiredDate || '')}</div>
          <div><b>Lugar de entrega</b></div><div>${esc(meta.delivery_place || '')}</div>
          <div><b>No. de requisición</b></div><div>${esc(meta.requisition_no || '')}</div>
          <div><b>No. de cotización</b></div><div>${esc(meta.quote_no || '')}</div>
        </div>
      </div>

      <div style="margin-top:12px">
        <table>
          <thead>
            <tr>
              <th class="th" style="width:54px">Ítem</th>
              <th class="th">Descripción</th>
              <th class="th right" style="width:90px">Cantidad</th>
              <th class="th right" style="width:120px">Vr. Unitario</th>
              <th class="th right" style="width:120px">Valor desc.</th>
              <th class="th right" style="width:110px">Impto. Cargo</th>
              <th class="th right" style="width:110px">Impto. Ret.</th>
              <th class="th right" style="width:120px">Vr. Total</th>
            </tr>
          </thead>
          <tbody>
            ${rows || ''}
          </tbody>
        </table>
      </div>

      <div class="grid2">
        <div class="box p12">
          <div class="small"><b>Observaciones</b></div>
          <div style="margin-top:6px; white-space:pre-wrap; word-break:break-word; line-height:1.25; min-height:72px">${esc(meta.observations || '')}</div>
          <div style="margin-top:10px" class="small"><b>Elaborado por</b></div>
          <div style="margin-top:6px">${esc(meta.prepared_by || '')}</div>
          <div style="margin-top:12px" class="small"><b>Aprobado por</b></div>
          <div style="margin-top:6px">${esc(meta.approved_by_name || '')}${meta.approved_at ? ` - ${esc(meta.approved_at)}` : ''}</div>
          <div style="margin-top:8px; min-height:72px; border-top:1px solid #d1d5db; padding-top:8px">
            ${approvalSigSrc ? `<img src="${esc(approvalSigSrc)}" alt="Firma aprobación" style="max-height:64px; max-width:220px; object-fit:contain; display:block" />` : `<div class="small">Sin firma de aprobación</div>`}
          </div>
        </div>
        <div class="box p12">
          <table class="totals">
            <tr><td class="lab">Subtotal</td><td class="val">${esc(fmtMoney(calc.subtotal))}</td></tr>
            <tr><td class="lab">Imp. Cargo ${esc(cargoLabel || `${(cargoRatio * 100).toFixed(2)}%`)}</td><td class="val">${esc(fmtMoney(calc.cargo))}</td></tr>
            <tr><td class="lab">Imp. Retención ${esc(retentionLabel || `${(retentionRatio * 100).toFixed(2)}%`)}</td><td class="val">${esc(fmtMoney(calc.retention))}</td></tr>
            <tr><td class="lab">Total a pagar (COP)</td><td class="val">${esc(fmtMoney(total))}</td></tr>
          </table>
        </div>
      </div>
      ${attachmentsHtml}
      ${(qualityVersion || qualityDate) ? `<div class="quality">Version ${esc(qualityVersion || '-')} | Vigencia ${esc(qualityDate || '-')}</div>` : ''}
    </div>
    <script>
      window.onload = () => { window.__OC_READY__ = true; };
    </script>
  </body>
  </html>`);
  doc.close();

  return { frame, w, title };
}

export async function generatePurchaseOrderPdfBlob({ order, request, meta, company, logoUrl }) {
  const { frame, w, title } = await openPurchaseOrderFrame({ order, request, meta, company, logoUrl });
  try {
    for (let i = 0; i < 60; i++) {
      if (w.__OC_READY__) break;
      await new Promise((r) => setTimeout(r, 50));
    }
    const imgs = Array.from(w.document.images || []);
    await Promise.all(imgs.map((img) => (
      img.complete ? Promise.resolve() : new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; })
    )));
    await new Promise((r) => setTimeout(r, 60));

    const el = w.document.querySelector('.sheet') || w.document.body;
    const ww = Math.max(1000, Number(el?.scrollWidth || 0) || 0);
    const wh = Math.max(1400, Number(el?.scrollHeight || 0) || 0);
    const renderScale = 2;
    const canvas = await html2canvas(el, {
      scale: renderScale,
      backgroundColor: '#FFFFFF',
      useCORS: true,
      windowWidth: ww,
      windowHeight: wh,
    });

    const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 6;
    const pxToMm = 0.26458333333333334;
    const mmW = (canvas.width / renderScale) * pxToMm;
    const mmH = (canvas.height / renderScale) * pxToMm;
    const maxW = pageW - (margin * 2);
    const pageInnerH = pageH - (margin * 2);
    const fitScale = Math.min(1, maxW / mmW);
    const drawW = mmW * fitScale;
    const drawH = mmH * fitScale;

    let offsetY = 0;
    pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
    while (offsetY + pageInnerH < drawH) {
      offsetY += pageInnerH;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', margin, margin - offsetY, drawW, drawH, undefined, 'FAST');
    }
    const blob = pdf.output('blob');
    return { blob, title };
  } finally {
    try { frame.remove(); } catch (_) {}
  }
}
